import React from 'react';
import classNames from 'classnames';
import {
  FDAlert,
  FDBox, FDContentContainer, FDHeadlineText, FDPaper, FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMobileLayout from 'Hooks/useMobileLayout';
import FirstYearAmountPicker
  from 'Views/Page/Content/Modal/Layouts/EstatePlanningC2BLayouts/EstatePlanningBuyPayment/Template/Layout/FirstYearAmountPicker/FirstYearAmountPicker';
import { injectStripe } from 'react-stripe-elements';
import { useSubmitHandler } from 'Views/Page/Content/Templates/WizardTemplates/Buy/useSubmitHandler';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { dataAttrs, DataValueMap } from 'Helpers/Testing';
import ErrorList from 'Components/UI/ErrorList';
import PropTypes from 'prop-types';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  EstatePlanningC2BHeader,
} from 'Views/Page/Content/Modal/Layouts/EstatePlanningC2BLayouts/components/EstatePlanningC2BHeader';

const useStyles = buildUseStyles('EstatePlanningRecommendationBuyStepLayout', (theme) => ({
  root: {
    '& [data-element-semantic="ep-c2b-header"] > [data-attr-name="title"]': {
      fontWeight: theme.typography.fontWeightExtraBold, // 700
    },
    '& [data-element-semantic="taxbuy subtitle"]': {
      color: theme.palette.facet.mint100p,
    },
    '& [data-attr-name="firstYearAmountWithDiscount"]': {
      fontWeight: theme.typography.fontWeightExtraBold, // 700
    },
    '& [data-attr-name="firstYearAmount"]': {
      fontWeight: theme.typography.fontWeightExtraBold, // 700
    },
  },
}));

const EstatePlanningRecommendationBuyStepLayout = ({ stripe }) => {
  const isMobile = useMobileLayout();
  const {
    resource,
    saving,
  } = useMetaLayoutContext();
  const { submitPageResource, setPageContentCollection, isPageContentLoading } = usePageContentContext();
  const {
    onSubmit,
    metaErrors,
    stripeError,
    loading,
  } = useSubmitHandler({ stripe });
  const classes = useStyles();

  const onSubmitPageResource = async () => {
    const { response, error: responseError } = await submitPageResource();
    if (!responseError) {
      setPageContentCollection(response);
    }
  };

  let error = null;
  if (stripeError) {
    error = (
      <FDAlert
        title="Error processing credit card"
        description={stripeError}
        status="error"
        {...dataAttrs('stripe error')}
      />
    );
  } else if (metaErrors.length) {
    error = <ErrorList errors={metaErrors} title="Error" />;
  }

  const canSubmit = resource.attr('initialFeeDisclaimer') === 'true';
  const isPaymentMethodNewCard = resource.attr('paymentMethod') === 'EstatePlanRecommendationPaymentMethodNewCard';

  return (
    <div className={classNames('buyWizard', classes.root)}> {/* This div is here to style the Stripe inputs. TODO: Move this logic to a common place. not buyWizard */}
      <FDBox paddingY={isMobile ? 6 : 8} paddingX={isMobile ? 2 : 6}>
        <FDContentContainer maxWidth={'md'}>
          <FDStack direction={'column'} spacing={6}>
            <FDStack direction={'column'} spacing={3}>
              <EstatePlanningC2BHeader />
              <FDStack direction={'column'}>
                <MetaControlField attr={'dueTodayText'} />
                <FirstYearAmountPicker resource={resource} />
                <MetaControlField attr={'firstYearText'} />
                <MetaControlField attr={'packageText'} />
                <FDBox paddingY={2}>
                  <MetaControlField attr={'packageDetailText'} />
                </FDBox>
              </FDStack>
            </FDStack>
            {resource.attr('includedListRichText') && (
              <FDStack direction={'column'} spacing={0} paddingY={1}>
                <FDHeadlineText value={'Included:'} size={'sm'} color={'textSecondary'} />
                <FDStack direction={'column'} paddingX={1}>
                  <MetaControlField attr={'includedListRichText'} />
                </FDStack>
              </FDStack>
            )}
            <FDPaper appearance={'elevated'} padding={3}>
              <FDStack flexGrow={1} direction={'column'} spacing={4} alignItems={'flex-start'}>
                <MetaControlField attr="paymentMethodText" />
                <MetaControlField attr="paymentMethod" />
                {resource.attr('paymentMethod') === 'EstatePlanRecommendationPaymentMethodNewCard' ? (
                  <MetaControlField attr="stripeToken" />
                ) : null}
                {!isMobile ? error : null}
                <FDStack direction={'column'}>
                  <FDStack spacing={2} direction={'column'} paddingY={2}>
                    <MetaControlField attr={'initialFeeDisclaimer'} />
                  </FDStack>
                </FDStack>
                <FDPrimaryActionButton
                  {...DataValueMap.init(`submit-btn${canSubmit ? '' : '-disabled'}`).build()}
                  label={resource.attr('primaryActionLabel') || 'Continue'}
                  loading={loading || saving || isPageContentLoading}
                  disabled={!canSubmit}
                  onClick={async () => {
                    // If we are using stripe, use its submit function
                    if (isPaymentMethodNewCard) {
                      await onSubmit();
                      return;
                    }

                    // Otherwise, submit the page resource
                    await onSubmitPageResource();
                  }}
                />
              </FDStack>
            </FDPaper>
            <MetaControlField attr={'disclosure'} />
          </FDStack>
        </FDContentContainer>
      </FDBox>
    </div>);
};

EstatePlanningRecommendationBuyStepLayout.propTypes = {
  stripe: PropTypes.object.isRequired,
};

export default injectStripe(EstatePlanningRecommendationBuyStepLayout);
